import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { CustomFieldAttribute } from 'apis/customFields';
import { HookFormQueuesSelect, HookFormText } from 'components/hookForm';
import { ElevionLeafFieldID, isLeafNode } from 'utils/elevion';

import { AttributeFormFieldValues } from '../../../../../../../customAttributeModalForm.types';

import { CustomAttributeLeafValueField } from './components/customAttributeLeafValueField/customAttributeLeafValueField';

export type LeafValueFieldProps = {
  parentName: string;
  customFields: CustomFieldAttribute[];
};

export const LeafValueField = ({
  parentName,
  customFields,
}: LeafValueFieldProps): JSX.Element | null => {
  // TODO: [TEAMS-13693] remove omit after updating to RHF v8
  const { control } =
    useFormContext<Omit<AttributeFormFieldValues, 'options'>>();
  const fieldName = `${parentName}.value` as 'visibility_condition.value';
  const node = useWatch({
    control,
    name: parentName as 'visibility_condition.nodes.0',
  });

  const shouldBeEmpty = isLeafNode(node) && node.comparator === 'is_empty';

  const valueType = useMemo(() => {
    // invalid node guard
    if (!isLeafNode(node)) return null;

    const isQueueSelect = node.field === ElevionLeafFieldID.Queue;
    if (isQueueSelect) return 'queue-select';

    const isCustomField = isLeafNode(node) && node.field.startsWith('custom__');
    if (isCustomField) {
      const fieldIdentifier = node.field.replace('custom__', '');
      const customField = customFields.find(
        (field) => field.identifier === fieldIdentifier,
      );
      if (!customField) return null;

      return `custom-${customField.type}`;
    }
  }, [node, customFields]);

  if (!isLeafNode(node)) return null;

  switch (valueType) {
    case 'queue-select': {
      return (
        <HookFormQueuesSelect
          name={fieldName}
          control={control}
          noAlert
          noLabel
          disabled={shouldBeEmpty}
          componentProps={{
            multiple: true,
          }}
        />
      );
    }

    case 'custom-select': {
      if (!Array.isArray(node.value)) return null;
      return (
        <CustomAttributeLeafValueField
          parentName={parentName}
          fieldName={fieldName}
          customFields={customFields}
          disabled={shouldBeEmpty}
        />
      );
    }

    case 'custom-text':
    default: {
      const placeholder = shouldBeEmpty ? 'No value needed' : 'Enter a value';
      return (
        <HookFormText
          name={fieldName}
          control={control}
          disabled={shouldBeEmpty}
          noLabel
          noAlert
          componentProps={{
            placeholder,
          }}
        />
      );
    }
  }
};
